import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { formatDate } from "../lib/format";
import {
  ArrowLeft, Truck, CheckCircle2, XCircle, Factory, Clock, Edit3, Save, Plus, Trash2, Printer,
} from "lucide-react";
import UserBadge from "../components/UserBadge";

const SIZE_OPTS = ["S","M","L","XL","2XL","3XL","4XL","5XL"];
const emptyItem = () => ({ brand: "", size: "", quantity: 1, unit: "cartons" });

// Full lifecycle order (used for progress arrow)
const FLOW = ["pending", "in_production", "dispatched", "completed"];

const STATUS_META = {
  pending:       { label: "Pending",        icon: Clock,        bar: "bg-yellow-50 text-yellow-800 border-yellow-300" },
  in_production: { label: "In Production",  icon: Factory,      bar: "bg-purple-50 text-purple-800 border-purple-300" },
  dispatched:    { label: "Dispatched",     icon: Truck,        bar: "bg-orange-50 text-orange-800 border-orange-300" },
  completed:     { label: "Completed",      icon: CheckCircle2, bar: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  cancelled:     { label: "Cancelled",      icon: XCircle,      bar: "bg-red-50 text-red-800 border-red-300" },
};

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItems, setEditingItems] = useState(false);
  const [itemsDraft, setItemsDraft] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [o, e, p] = await Promise.all([api.get(`/orders/${id}`), api.get("/employees"), api.get("/products")]);
    setOrder(o.data); setEmployees(e.data); setProducts(p.data);
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (payload, successMsg = "Order updated") => {
    try {
      const { data } = await api.patch(`/orders/${id}`, payload);
      setOrder(data);
      toast.success(successMsg);
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  };

  const advance = async (nextStatus, label) => {
    if (!window.confirm(`Move this order to "${label}"?`)) return;
    await patch({ status: nextStatus }, `Order marked as ${label}`);
  };

  const complete = async () => {
    if (!window.confirm("Mark this order as COMPLETED? Use this after the buyer has received the goods.")) return;
    await patch({ status: "completed" }, "Order completed 🎉");
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this order? This cannot be undone (only delete after).")) return;
    await patch({ status: "cancelled" }, "Order cancelled");
  };

  if (loading || !order) {
    return <div className="text-ink-soft font-figtree">Loading…</div>;
  }

  const meta = STATUS_META[order.status] || STATUS_META.pending;
  const StatusIcon = meta.icon;
  const isFinal = order.status === "completed" || order.status === "cancelled";
  const overdue = order.dispatch_by && !isFinal && order.status !== "dispatched"
    && new Date(order.dispatch_by) < new Date(new Date().toDateString());
  const items = order.items || [];
  const currentIdx = FLOW.indexOf(order.status);
  const nextStatus = currentIdx >= 0 && currentIdx < FLOW.length - 1 ? FLOW[currentIdx + 1] : null;

  const openPrint = () => window.open(`/orders/${order.id}/print`, "_blank", "noopener,noreferrer");

  return (
    <div data-testid="order-detail-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-ink-soft hover:text-forest font-figtree font-medium mb-4 transition-colors duration-200">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <PageHeader
        title={`Order for ${order.party_name}`}
        subtitle={`Received on ${formatDate(order.received_at)} · ${items.length} item${items.length !== 1 ? "s" : ""}`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={openPrint} data-testid="order-print-btn" className="px-3 py-2 rounded-lg border border-line font-figtree font-medium text-sm hover:bg-sand transition-colors duration-200 flex items-center gap-2">
              <Printer className="h-4 w-4" /> Print
            </button>
            {order.status !== "completed" && order.status !== "cancelled" && (
              <button onClick={complete} data-testid="order-complete-btn" className="btn-primary flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Mark Completed
              </button>
            )}
          </div>
        }
      />

      {/* Attribution strip: who created and last edited this order */}
      <div className="card-surface px-4 py-3 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="order-attribution">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <UserBadge user={order.created_by} label="Created by" size="lg" testid="order-created-by" />
          {order.last_edited_by && order.last_edited_by.id && order.last_edited_by.id !== order.created_by?.id && (
            <UserBadge user={order.last_edited_by} label="Last edited by" size="lg" testid="order-edited-by" />
          )}
        </div>
        {order.last_edited_at && (
          <div className="text-xs font-figtree text-ink-mute">Updated {formatDate(order.last_edited_at)}</div>
        )}
      </div>

      {/* Status banner */}
      <div className={`card-surface p-6 mb-6 border-l-4 ${meta.bar}`} data-testid="order-status-banner">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-white grid place-items-center border border-line">
              <StatusIcon className="h-6 w-6" />
            </div>
            <div>
              <div className="font-figtree text-sm opacity-80">Current status</div>
              <div className="font-outfit font-bold text-2xl mt-0.5">{meta.label}</div>
              {overdue && <div className="text-sm text-red-700 font-figtree font-semibold mt-1">Overdue — deadline was {formatDate(order.dispatch_by)}</div>}
            </div>
          </div>

          {!isFinal && (
            <div className="flex flex-wrap gap-2" data-testid="order-status-actions">
              {nextStatus && (
                <button
                  onClick={() => advance(nextStatus, STATUS_META[nextStatus].label)}
                  data-testid={`advance-to-${nextStatus}`}
                  className="btn-primary flex items-center gap-2 text-sm"
                >
                  Move to {STATUS_META[nextStatus].label}
                </button>
              )}
              <button
                onClick={cancel}
                data-testid="order-cancel-btn"
                className="px-3 py-2 rounded-lg border border-red-200 text-red-700 font-figtree font-medium text-sm hover:bg-red-50 transition-colors duration-200 flex items-center gap-1.5"
              >
                <XCircle className="h-4 w-4" /> Cancel Order
              </button>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {order.status !== "cancelled" && (
          <div className="mt-5 pt-5 border-t border-white/40">
            <div className="flex items-center gap-2 flex-wrap">
              {FLOW.map((s, i) => {
                const Sicon = STATUS_META[s].icon;
                const done = i <= currentIdx;
                return (
                  <div key={s} className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-full grid place-items-center flex-shrink-0 ${done ? "bg-forest text-white" : "bg-white text-ink-mute border border-line"}`}>
                      <Sicon className="h-4 w-4" />
                    </div>
                    <span className={`text-xs font-figtree ${done ? "text-ink font-semibold" : "text-ink-mute"}`}>{STATUS_META[s].label}</span>
                    {i < FLOW.length - 1 && <div className={`h-0.5 w-6 sm:w-10 ${i < currentIdx ? "bg-forest" : "bg-line"}`} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-surface p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-semibold text-lg text-ink">Line Items</h3>
            {!isFinal && !editingItems && (
              <button
                onClick={() => { setItemsDraft(items.map(it => ({ ...it }))); setEditingItems(true); }}
                data-testid="order-edit-items-btn"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-line font-figtree font-medium text-sm hover:bg-sand transition-colors duration-200"
              >
                <Edit3 className="h-4 w-4" /> Edit Order
              </button>
            )}
            {editingItems && (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingItems(false)}
                  className="px-3 py-2 rounded-lg border border-line font-figtree font-medium text-sm"
                >Cancel</button>
                <button
                  onClick={async () => {
                    const cleaned = itemsDraft.filter(it => it.brand.trim() && it.size.trim() && parseInt(it.quantity,10) > 0)
                      .map(it => ({ brand: it.brand.trim(), size: it.size.trim(), quantity: parseInt(it.quantity,10), unit: it.unit || "cartons" }));
                    if (cleaned.length === 0) { toast.error("Add at least one valid item"); return; }
                    const ok = await patch({ items: cleaned });
                    if (ok) setEditingItems(false);
                  }}
                  data-testid="order-save-items-btn"
                  className="btn-primary flex items-center gap-2 text-sm"
                ><Save className="h-4 w-4" /> Save</button>
              </div>
            )}
          </div>
          {!editingItems ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-sand/40">
                  <tr>
                    <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-3 py-3">Brand</th>
                    <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-3 py-3">Size</th>
                    <th className="text-right font-figtree font-semibold text-sm text-ink-soft px-3 py-3">Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className="border-t border-line">
                      <td className="px-3 py-3 font-figtree font-medium text-ink">{it.brand}</td>
                      <td className="px-3 py-3 font-figtree text-ink">{it.size}</td>
                      <td className="px-3 py-3 text-right font-outfit font-semibold text-ink whitespace-nowrap">{it.quantity} <span className="text-xs font-normal text-ink-soft">{it.unit || "cartons"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rounded-lg border border-line overflow-hidden">
              <div className="grid grid-cols-12 gap-2 bg-sand/60 px-3 py-2 text-xs font-figtree font-semibold text-ink-soft">
                <div className="col-span-4">Brand</div><div className="col-span-2">Size</div><div className="col-span-2">Qty</div><div className="col-span-3">Unit</div><div className="col-span-1"></div>
              </div>
              <div className="divide-y divide-line">
                {itemsDraft.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`order-edit-row-${idx}`}>
                    <input data-testid={`order-edit-brand-${idx}`} value={it.brand} onChange={(e)=>setItemsDraft(d=>d.map((x,i)=>i===idx?{...x,brand:e.target.value}:x))} list="order-detail-products" placeholder="Brand" className="col-span-4 h-10 px-2 rounded border border-line outline-none focus:ring-1 focus:ring-forest text-sm" />
                    <select data-testid={`order-edit-size-${idx}`} value={it.size} onChange={(e)=>setItemsDraft(d=>d.map((x,i)=>i===idx?{...x,size:e.target.value}:x))} className="col-span-2 h-10 px-2 rounded border border-line bg-white outline-none focus:ring-1 focus:ring-forest text-sm">
                      <option value="">Size</option>
                      {SIZE_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input data-testid={`order-edit-qty-${idx}`} type="number" min="1" value={it.quantity} onChange={(e)=>setItemsDraft(d=>d.map((x,i)=>i===idx?{...x,quantity:e.target.value}:x))} className="col-span-2 h-10 px-2 rounded border border-line outline-none focus:ring-1 focus:ring-forest text-sm" />
                    <select data-testid={`order-edit-unit-${idx}`} value={it.unit} onChange={(e)=>setItemsDraft(d=>d.map((x,i)=>i===idx?{...x,unit:e.target.value}:x))} className="col-span-3 h-10 px-2 rounded border border-line bg-white outline-none focus:ring-1 focus:ring-forest text-sm">
                      <option value="cartons">cartons</option>
                      <option value="pacs">pacs</option>
                    </select>
                    <div className="col-span-1 flex justify-end">
                      {itemsDraft.length > 1 && (
                        <button onClick={()=>setItemsDraft(d=>d.filter((_,i)=>i!==idx))} data-testid={`order-edit-remove-${idx}`} className="p-1 rounded text-ink-soft hover:bg-red-50 hover:text-red-700">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={()=>setItemsDraft(d=>[...d, emptyItem()])}
                data-testid="order-edit-add-row"
                className="w-full py-2 text-sm font-figtree font-semibold text-forest hover:bg-forest-soft flex items-center justify-center gap-1 border-t border-line"
              ><Plus className="h-4 w-4" /> Add Row</button>
              <datalist id="order-detail-products">
                {products.map(p => <option key={p.id} value={p.name} />)}
              </datalist>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6 pt-6 border-t border-line">
            <Detail label="Buyer" value={<Link to={`/parties/${order.party_id}`} className="text-forest hover:underline">{order.party_name}</Link>} />
            <Detail
              label="Assigned Employee"
              value={
                <select
                  data-testid="order-employee-select"
                  value={order.employee_id || ""}
                  onChange={(e) => patch({ employee_id: e.target.value })}
                  disabled={isFinal}
                  className="w-full h-10 px-3 rounded-lg border border-line bg-white outline-none focus:ring-2 focus:ring-forest disabled:bg-sand disabled:cursor-not-allowed"
                >
                  <option value="">None</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              }
            />
            <Detail
              label="Order Date"
              value={
                <input
                  type="date"
                  data-testid="order-date-edit"
                  value={order.received_at ? order.received_at.slice(0, 10) : ""}
                  onChange={(e) => patch({ order_date: e.target.value })}
                  disabled={isFinal}
                  className="w-full h-10 px-3 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest disabled:bg-sand disabled:cursor-not-allowed"
                />
              }
            />
            <Detail
              label="Dispatch By"
              value={
                <input
                  type="date"
                  data-testid="order-dispatch-date"
                  value={order.dispatch_by ? order.dispatch_by.slice(0, 10) : ""}
                  onChange={(e) => patch({ dispatch_by: e.target.value })}
                  disabled={isFinal}
                  className="w-full h-10 px-3 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest disabled:bg-sand disabled:cursor-not-allowed"
                />
              }
            />
            <Detail label="Notes" value={order.notes || "—"} />
          </div>
        </div>

        <div className="card-surface p-6">
          <h3 className="font-outfit font-semibold text-lg text-ink mb-4">Timeline</h3>
          <ul className="space-y-4">
            <TimelineItem done icon={Clock} label="Order Received" date={formatDate(order.received_at)} />
            <TimelineItem done={currentIdx >= 1} icon={Factory} label="In Production" />
            <TimelineItem done={currentIdx >= 2} icon={Truck} label="Dispatched" date={order.dispatched_at ? formatDate(order.dispatched_at) : ""} />
            <TimelineItem done={order.status === "completed"} icon={CheckCircle2} label="Completed" date={order.completed_at ? formatDate(order.completed_at) : ""} />
            {order.status === "cancelled" && <TimelineItem done danger icon={XCircle} label="Cancelled" />}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="font-figtree text-sm text-ink-soft mb-1">{label}</div>
      <div className="font-figtree text-ink">{value}</div>
    </div>
  );
}

function TimelineItem({ done, danger, icon: Icon, label, date }) {
  return (
    <li className="flex items-start gap-3">
      <div className={`h-9 w-9 rounded-full grid place-items-center flex-shrink-0
        ${danger ? "bg-red-100 text-red-700" : done ? "bg-forest text-white" : "bg-sand text-ink-mute"}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="pt-1">
        <div className={`font-figtree font-medium ${done ? "text-ink" : "text-ink-mute"}`}>{label}</div>
        {date && <div className="text-xs text-ink-soft font-figtree">{date}</div>}
      </div>
    </li>
  );
}
